import { Transform, Duplex, PassThrough } from 'stream';
const HEADER_SIZE = 6;
// 16KB minus some bytes due to headers
// See section 6.6 https://datatracker.ietf.org/doc/rfc8831/
const MAX_CHUNK_SIZE = (16 * 1024) - 64;
function encodeHeader(index, total) {
    const header = Buffer.alloc(HEADER_SIZE);
    header.writeUInt16BE(index, 0);
    header.writeUInt16BE(total, 2);
    return header;
}
function decodeHeader(buf) {
    return {
        index: buf.readUInt16BE(0),
        total: buf.readUInt16BE(2),
    };
}
export class ChunkEncoder extends Transform {
    _transform(data, _enc, cb) {
        const buf = Buffer.isBuffer(data) ? data : Buffer.from(data);
        const totalChunks = Math.ceil(buf.length / MAX_CHUNK_SIZE);
        for (let i = 0; i < totalChunks; i++) {
            const payload = buf.slice(i * MAX_CHUNK_SIZE, (i + 1) * MAX_CHUNK_SIZE);
            const header = encodeHeader(i, totalChunks);
            const data = Buffer.concat([header, payload]);
            this.push(data);
        }
        cb();
    }
}
export class ChunkDecoder extends Transform {
    constructor() {
        super(...arguments);
        this.current = null;
    }
    _transform(chunk, _enc, cb) {
        if (chunk.length < HEADER_SIZE) {
            const error = new Error('Chunk too small');
            error.code = 'CHUNK_DECODE_ERROR';
            cb(error);
            return;
        }
        const header = decodeHeader(chunk.slice(0, HEADER_SIZE));
        const payload = chunk.slice(HEADER_SIZE);
        if (!this.current) {
            this.current = {
                total: header.total,
                chunks: new Array(header.total),
            };
        }
        this.current.chunks[header.index] = payload;
        if (this.current.chunks.filter(Boolean).length === this.current.total) {
            const full = Buffer.concat(this.current.chunks);
            this.push(full);
            this.current = null;
        }
        cb();
    }
}
export function wrapWithChunking(base) {
    const encoder = new ChunkEncoder();
    const decoder = new ChunkDecoder();
    const input = new PassThrough();
    const output = new PassThrough();
    // Pipe input → encoder → base transport
    input.pipe(encoder).pipe(base);
    // Pipe base transport → decoder → output
    base.pipe(decoder).pipe(output);
    // Forward errors
    const forwardError = (source, target) => {
        source.on('error', (err) => target.emit('error', err));
    };
    forwardError(encoder, base);
    forwardError(decoder, base);
    // Create the final wrapped Duplex stream
    const wrapped = new Duplex({
        write(chunk, _enc, cb) {
            input.write(chunk, cb);
        },
        final(cb) {
            input.end(cb);
        },
        read() {
            let chunk;
            while ((chunk = output.read()) !== null) {
                this.push(chunk);
            }
        },
        destroy(err, cb) {
            input.destroy();
            output.destroy();
            base.destroy();
            if (cb)
                cb(err);
        }
    });
    // Push data from output into wrapped stream
    output.on('readable', function () {
        let chunk;
        while ((chunk = output.read()) !== null) {
            wrapped.push(chunk);
        }
    });
    // End wrapped readable side when output ends
    output.on('end', () => {
        wrapped.push(null);
    });
    return wrapped;
}
